import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import {
  Box,
  Icon,
  Flex,
  Stack,
  Text,
  Heading,
  SimpleGrid
} from '@chakra-ui/react';
import {
  BiRuler,
  BiDollar,
  BiCalendar
} from 'react-icons/bi';
import {
  AiFillPrinter,
  AiOutlineLineChart,
  AiOutlineVerticalAlignTop,
  AiOutlinePlusCircle,
} from 'react-icons/ai';
import {
  GiThorHammer,
  GiMaterialsScience
} from 'react-icons/gi';
import {
  FaRegClone
} from 'react-icons/fa';
import {
  RiScalesLine
} from 'react-icons/ri';
import {
  MdOutlineLocalOffer
} from 'react-icons/md';
import {
  ItemInfoBlock,
  TabGroup,
  Slider,
  EmptyState,
  Modal,
  Drawer,
  ItemContainer,
  ItemRankChangeWizard,
  GroupingCreationWizard,
  ItemContainerSkeleton,
  TradingFloor,
  InfoGuide,
  PaginatedListingGrid,
  ListingGrid,
  ItemCard,
  ItemGridSkeleton,
  StatsCard,
  MigrateImageWizard
} from 'components';
import ItemDataSuggestionForm, { ItemDataSuggestionFormData, ItemDataSuggestionFormOption } from 'components/Form/ItemDataSuggestionForm/ItemDataSuggestionForm';
import { ItemModel, ListingModel, ItemBidModel, ItemAskModel, ArtistModel, PaginationModel } from 'models';
import { JSONObject } from 'models/Api/types';
import { convertJSONObjectToSnakecase } from 'utils/object';
import { NavigationService } from 'services';
import { QuerySearchParameters } from 'constants/url';
import { getEligibleQueryParamsFromUrl } from 'utils/url';
import { useItem, useItemListings, useItemMarket, useItemSuggestions, useDemotedItem, useUpdateItem, useDemoteItem, usePromoteItem, useItemActiveMarket, useRefreshItemMarket, useEnhanceItem, useRemoveItemBackground, useMigrateItemImage } from 'hooks/queries/useItems';
import { useMaterials } from 'hooks/queries/useMaterials';
import { useSalesVenues } from 'hooks/queries/useSalesVenues';
import { useItemSuggestedListings, useRecomputeItemSuggestions } from 'hooks/queries';
import { useCreateGrouping } from 'hooks/queries/useGroupings';
import { useListingSuggestionsForItem } from 'hooks/queries/useListings';
import MarketPriceChart from 'components/Charts/MarketPriceChart';
import MarketVolumeChart from 'components/Charts/MarketVolumeChart';
import { useArtistByItem } from 'hooks/queries';
import { IoMdStats } from 'react-icons/io';
import { IoStorefrontOutline } from 'react-icons/io5';

interface ItemDetailsViewModalState {
  itemEditModal: boolean;
  itemEditFieldModal: ItemDataSuggestionFormOption | undefined;
  rankChange: boolean;
  itemBid: boolean;
  itemAsk: boolean;
  removeItemBid: ItemBidModel | undefined;
  removeItemAsk: ItemAskModel | undefined;
  editItemBid: ItemBidModel | undefined;
  editItemAsk: ItemAskModel | undefined;
  contactItemBid: ItemBidModel | undefined;
  contactItemAsk: ItemAskModel | undefined;
  sellItem: ItemBidModel | undefined;
  buyItem: ItemAskModel | undefined;
  createGrouping: boolean;
  migrateImage: boolean;
}

interface ItemDetailsViewDrawerState {
  marketInfoGuide: boolean;
}

interface ItemDetailsViewMarketplaceState {
  selectedAskForPayment: string | undefined;
}

export enum ItemDetailsSizingConfig {
  SECTION_SPACING = '40px',
  CONTENT_SPACING = '20px',
  MARKET_BUTTON_HEIGHT = '48px',
  MARKET_BUTTON_WIDTH = '40%',
  ITEM_IMAGE_MAX_HEIGHT = '480px'
}

export function ItemDetailsView() {
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const { currentUser } = useAuth();

  // State management
  const [modals, setModals] = useState<ItemDetailsViewModalState>({
    itemEditModal: false,
    itemEditFieldModal: undefined,
    rankChange: false,
    itemBid: false,
    itemAsk: false,
    removeItemBid: undefined,
    removeItemAsk: undefined,
    editItemBid: undefined,
    editItemAsk: undefined,
    contactItemBid: undefined,
    contactItemAsk: undefined,
    sellItem: undefined,
    buyItem: undefined,
    createGrouping: false,
    migrateImage: false
  });
  const [drawers, setDrawers] = useState<ItemDetailsViewDrawerState>({
    marketInfoGuide: false
  });
  const [marketplace, setMarketplace] = useState<ItemDetailsViewMarketplaceState>({
    selectedAskForPayment: undefined
  });
  const [pendingQueryParams, setPendingQueryParams] = useState<JSONObject | null>(null);
  const [listingSuggestionsPage, setListingSuggestionsPage] = useState(1);


  // Check if this is a demoted item
  const urlParams = new URLSearchParams(location.search);
  const isDemoted = urlParams.get('type') === 'demoted';

  // React Query hooks - call both but only enable the appropriate one
  const { data: regularItem, isLoading: regularItemLoading } = useItem(params.id!, { enabled: !isDemoted });
  const { data: demotedItem, isLoading: demotedItemLoading } = useDemotedItem(params.id!, { enabled: isDemoted });

  // Use the appropriate item based on type
  const item = isDemoted ? demotedItem : regularItem;
  const itemLoading = isDemoted ? demotedItemLoading : regularItemLoading;

  // Only fetch related data for regular items, not demoted ones
  const { data: artist } = useArtistByItem(params.id!, { enabled: !isDemoted });
  const { data: itemMarket, isLoading: itemMarketLoading } = useItemMarket(params.id!, { enabled: !isDemoted });
  const { data: itemActiveMarket, isLoading: itemActiveMarketLoading } = useItemActiveMarket(params.id!, { enabled: !isDemoted });
  const { data: itemSuggestions, isLoading: itemSuggestionsLoading } = useItemSuggestions(params.id!, { enabled: !isDemoted });
  const { data: itemListings = [], isLoading: itemListingsLoading } = useItemListings(params.id!, { enabled: !isDemoted });
  const { data: materialList = [] } = useMaterials();
  useSalesVenues();

  // Intelligence API suggestions
  const { data: paginatedListingSuggestions, isLoading: intelligenceSuggestionsLoading } = useItemSuggestedListings(
    params.id!,
    { page: listingSuggestionsPage },
    !isDemoted
  );

  // Search-based suggestions (fallback)
  const { data: searchBasedSuggestions, isLoading: searchSuggestionsLoading } = useListingSuggestionsForItem(
    item?.name || '',
    artist?.name || '',
    listingSuggestionsPage,
    !isDemoted && !!item && !!artist
  );

  // Transform suggested listings to just listings for display
  const listingSuggestions = useMemo(() => {
    // Use intelligence API results if available and not empty
    if (paginatedListingSuggestions && paginatedListingSuggestions.data.length > 0) {
      const listings = paginatedListingSuggestions.data.map(suggestion => suggestion.listing);
      return new PaginationModel({
        ...paginatedListingSuggestions,
        data: listings,
        current_page: paginatedListingSuggestions.currentPage,
        per_page: paginatedListingSuggestions.perPage,
        total_count: paginatedListingSuggestions.totalCount,
        total_pages: paginatedListingSuggestions.totalPages,
        fetched: paginatedListingSuggestions.fetchedData
      });
    }

    // Fallback to search-based suggestions
    return searchBasedSuggestions;
  }, [paginatedListingSuggestions, searchBasedSuggestions]);

  const listingSuggestionsLoading = intelligenceSuggestionsLoading || searchSuggestionsLoading;

  // Mutations
  const updateItemMutation = useUpdateItem(params.id!);
  const demoteItemMutation = useDemoteItem();
  const promoteItemMutation = usePromoteItem();
  const refreshItemMarketMutation = useRefreshItemMarket();
  const enhanceItemMutation = useEnhanceItem();
  const removeItemBackgroundMutation = useRemoveItemBackground();
  const migrateItemImageMutation = useMigrateItemImage();
  const createGroupingMutation = useCreateGrouping();
  const recomputeItemSuggestionsMutation = useRecomputeItemSuggestions();

  // Effects
  useEffect(() => {
    if (item && itemMarket) {
      item.setItemMarket(itemMarket);
    }
  }, [item, itemMarket]);

  useEffect(() => {
    if (item && itemActiveMarket) {
      item.setItemActiveMarket(itemActiveMarket);
    }
  }, [item, itemActiveMarket]);

  useEffect(() => {
    if (params.id) {
      const queryParams = getEligibleQueryParamsFromUrl(location, [
        QuerySearchParameters.BID_ID,
        QuerySearchParameters.ASK_ID,
        QuerySearchParameters.ACTION
      ]);
      if (queryParams) {
        setPendingQueryParams(queryParams);
      }
    }
  }, [params.id, location]);

  const processActiveMarketQueryParams = useCallback(() => {
    if (!pendingQueryParams || !item) return;

    const { bidId, askId, action } = pendingQueryParams;
    const matchingBid = item.getBids().find((bid) => bid.id === bidId);
    const matchingAsk = item.getAsks().find((ask) => ask.id === askId);

    if (matchingBid) {
      switch (action) {
        case 'Edit':
          setModals(prev => ({ ...prev, editItemBid: matchingBid }));
          break;
        case 'Remove':
          setModals(prev => ({ ...prev, removeItemBid: matchingBid }));
          break;
        case 'Contact':
          setModals(prev => ({ ...prev, contactItemBid: matchingBid }));
          break;
        case 'Sell':
          setModals(prev => ({ ...prev, sellItem: matchingBid }));
          break;
      }
    }

    if (matchingAsk) {
      switch (action) {
        case 'Edit':
          setModals(prev => ({ ...prev, editItemAsk: matchingAsk }));
          break;
        case 'Remove':
          setModals(prev => ({ ...prev, removeItemAsk: matchingAsk }));
          break;
        case 'Contact':
          setModals(prev => ({ ...prev, contactItemAsk: matchingAsk }));
          break;
        case 'Buy':
          setModals(prev => ({ ...prev, buyItem: matchingAsk }));
          break;
      }
    }

    setPendingQueryParams(null);
  }, [pendingQueryParams, item]);

  useEffect(() => {
    if (pendingQueryParams && item?.hasActiveMarketData()) {
      processActiveMarketQueryParams();
    }
  }, [pendingQueryParams, item, itemActiveMarket, processActiveMarketQueryParams]);

  useEffect(() => {
    if (marketplace.selectedAskForPayment) {
      const currentAsk = item?.activeMarket?.asks.find(
        ask => ask.id === marketplace.selectedAskForPayment
      );
      if (currentAsk?.paymentLink) {
        window.open(currentAsk.paymentLink, '_blank');
        setMarketplace({ selectedAskForPayment: undefined });
      }
    }
  }, [marketplace.selectedAskForPayment, item]);

  // Event handlers
  const onItemSuggestionGridClick = (item: ItemModel) => {
    navigate(NavigationService.getItemDetailsPath(item.id, item.getDisplayName()));
  };

  // Modal handlers

  const onEditItemBidModalOpen = (bid: ItemBidModel) => {
    setModals(prev => ({
      ...prev,
      editItemBid: bid
    }));
  };

  const onEditItemAskModalOpen = (ask: ItemAskModel) => {
    setModals(prev => ({
      ...prev,
      editItemAsk: ask
    }));
  };

  const onRemoveItemBidModalOpen = (bid: ItemBidModel) => {
    setModals(prev => ({
      ...prev,
      removeItemBid: bid
    }));
  };

  const onRemoveItemAskModalOpen = (ask: ItemAskModel) => {
    setModals(prev => ({
      ...prev,
      removeItemAsk: ask
    }));
  };

  const onContactItemBidModalOpen = (bid: ItemBidModel) => {
    setModals(prev => ({
      ...prev,
      contactItemBid: bid
    }));
  };

  const onContactItemAskModalOpen = (ask: ItemAskModel) => {
    setModals(prev => ({
      ...prev,
      contactItemAsk: ask
    }));
  };

  const onSellItemModelOpen = (bid: ItemBidModel) => {
    setModals(prev => ({
      ...prev,
      sellItem: bid
    }));
  };

  const onBuyItemModelOpen = (ask: ItemAskModel) => {
    setModals(prev => ({
      ...prev,
      buyItem: ask
    }));
  };

  // Click handlers
  const onEditClick = () => {
    setModals(prev => ({ ...prev, itemEditModal: true }));
  };

  const onItemEditModalClose = () => {
    setModals(prev => ({ ...prev, itemEditModal: false }));
  };

  const onRankChangeClick = () => {
    setModals(prev => ({ ...prev, rankChange: true }));
  };

  const onRankChangeModalClose = () => {
    setModals(prev => ({ ...prev, rankChange: false }));
  };

  const onCreateGroupingClick = () => {
    setModals(prev => ({ ...prev, createGrouping: true }));
  };

  const onCreateGroupingModalClose = () => {
    setModals(prev => ({ ...prev, createGrouping: false }));
  };

  const onCreateGroupingSubmit = (item: ItemModel, listings: ListingModel[]) => {
    createGroupingMutation.mutate({
      item_id: item.id,
      listings: listings.map(listing => listing.id),
      auto_link: true
    });
    onCreateGroupingModalClose();
  };

  const onMigrateImageModalOpen = () => {
    setModals(prev => ({ ...prev, migrateImage: true }));
  };

  const onMigrateImageModalClose = () => {
    setModals(prev => ({ ...prev, migrateImage: false }));
  };

  const handleMigrateImageSubmit = (entity: ItemModel | ListingModel | ArtistModel, newImageUrl: string) => {
    if (!item) {
      onMigrateImageModalClose();
      return;
    }

    migrateItemImageMutation.mutate(
      { id: item.id, imageUrl: newImageUrl },
      {
        onSuccess: () => {
          onMigrateImageModalClose();
        }
      }
    );
  };

  const onRankChangeSubmit = async (item: ItemModel, reason: string) => {
    if (isDemoted) {
      const newItemId = await promoteItemMutation.mutateAsync({ id: item.id, reason });
      // Navigate to the new promoted item
      navigate(NavigationService.getItemDetailsPath(newItemId, item.getDisplayName()));
    } else {
      const newItemId = await demoteItemMutation.mutateAsync({ id: item.id, reason });
      // Navigate to the new demoted item
      navigate(NavigationService.getItemDetailsPath(newItemId, item.getDisplayName(), true));
    }
  };

  const onItemNameClick = (itemId: string, itemName: string) => {
    navigate(NavigationService.getItemDetailsPath(itemId, itemName));
  };

  const onArtistClick = () => {
    if (artist) {
      navigate(NavigationService.getArtistDetailsPath(artist.id, artist.name));
    }
  };

  const onItemEditFieldModalOpen = (field: ItemDataSuggestionFormOption) => {
    setModals(prev => ({ ...prev, itemEditFieldModal: field }));
  };

  const onItemEditFieldModalClose = () => {
    setModals(prev => ({ ...prev, itemEditFieldModal: undefined }));
  };

  const onItemEditFormSubmit = (data: ItemDataSuggestionFormData) => {
    const snakeCasedData: JSONObject = convertJSONObjectToSnakecase(data as JSONObject);
    updateItemMutation.mutate(snakeCasedData);
    onItemEditModalClose();
    onItemEditFieldModalClose();
  };

  // Helper functions
  const getItemMaterialNames = (): string | undefined => {
    if (!item || !item.getMaterialIds().length || !materialList.length) {
      return undefined;
    }

    const materialNames = item.getMaterialIds()
      .map(materialId => {
        const material = materialList.find(m => m.id === materialId);
        return material?.name;
      })
      .filter(Boolean);

    return materialNames.length > 0 ? materialNames.join(', ') : undefined;
  };

  const getItemEditFormData = (): ItemDataSuggestionFormData => {
    if (!item) return {};

    return {
      releasePriceAmount: item.getReleasePriceAmount(),
      releasePriceCurrency: item.getReleasePriceCurrency('USD'),
      publisher: item.publisher,
      material: item.material,
      materialIds: item.getMaterialIds() || [],
      edition: item.getEditionSize() || undefined,
      date: item.getReleaseDate()?.format('YYYY-MM-DD'),
      dimensions: item.dimensions || undefined,
      name: item.name || undefined,
      type: item.type || undefined
    };
  };

  // Render functions
  const renderItemInfoSlider = () => {
    if (!item) return null;

    const content = [
      { label: 'Size', value: item.getDimensionsForDisplay(), icon: BiRuler, field: 'dimensions' as ItemDataSuggestionFormOption },
      { label: 'Edition Size', value: item.edition, icon: FaRegClone, field: 'edition' as ItemDataSuggestionFormOption },
      { label: 'Release Year', value: item.hasReleaseDate() ? String(item.getReleaseYear()) : undefined, icon: BiCalendar, field: 'date' as ItemDataSuggestionFormOption },
      { label: 'Retail Price', value: item.releasePrice, icon: BiDollar, field: 'releasePrice' as ItemDataSuggestionFormOption },
      { label: 'Publisher', value: item.publisher, icon: AiFillPrinter, field: 'publisher' as ItemDataSuggestionFormOption },
      { label: 'Material', value: getItemMaterialNames(), icon: GiMaterialsScience, field: 'materialIds' as ItemDataSuggestionFormOption },
    ].map((block, index: number) => (
      <ItemInfoBlock
        key={`item_info_block_${index}`}
        label={block.label}
        value={block.value}
        icon={block.icon}
        fallbackIcon={AiOutlinePlusCircle}
        onFallbackContentClick={() => onItemEditFieldModalOpen(block.field)}
        editable={true}
        onEditClick={() => onItemEditFieldModalOpen(block.field)}
        isLoading={itemLoading}
      />
    ));

    return (
      <Box marginTop={ItemDetailsSizingConfig.SECTION_SPACING}>
        <Slider content={content} slidesPerView={4} enableAutoPlay={false} slidesPerMobileView={1} />
      </Box>
    );
  };

  const renderItemSuggestionGrid = () => {
    // Don't render if no data and not loading
    if (!itemSuggestionsLoading && !itemSuggestions) {
      return null;
    }

    const content = itemSuggestionsLoading ? (
      <ItemGridSkeleton rows={1} gridDisplay={false} />
    ) : (
      <Slider content={
        itemSuggestions.map(
          (item: ItemModel, index: number) =>
            <ItemCard onClick={() => onItemSuggestionGridClick(item)} key={`item_suggestions_card_${index}`} item={item} clickOnImage clickOnName />
        )
      } slidesPerView={4} slidesPerMobileView={2} />
    );

    return (
      <Flex direction="column" gap={4} textAlign={'left'} marginY={8}>
        <Heading fontSize={{ base: 'xl', md: '2xl' }} padding={{ base: 3, md: 0 }}> Related Works </Heading>
        {content}
      </Flex>
    );
  };

  const handleListingNameClick = (id: string, listingName: string) => {
    navigate(NavigationService.getListingDetailsPath(id, listingName));
  };

  const renderListingSuggestions = () => {
    return (
      <PaginatedListingGrid
        paginatedListings={listingSuggestions}
        isLoading={listingSuggestionsLoading}
        onPageSelect={setListingSuggestionsPage}
        onListingNameClick={handleListingNameClick}
        onListingArtistClick={onArtistClick}
        scrollMode={false}
        showAssociatedItems={false}
        showAssociationAction={false}
        onRefreshClick={!isDemoted ? () => recomputeItemSuggestionsMutation.mutate(params.id!) : undefined}
        refreshLoading={recomputeItemSuggestionsMutation.isPending}
        refreshTooltip="Recompute Suggestions"
      />
    );
  };

  const renderContainer = () => {
    if (!item) return null;
    return (
      <ItemContainer
        item={item}
        artist={artist}
        isFollowingArtist={false}
        followArtistLoading={false}
        onFollowArtistClick={() => { }}
        onArtistClick={onArtistClick}
        onEditClick={onEditClick}
        onRankChangeClick={onRankChangeClick}
        onGroupingClick={onCreateGroupingClick}
        isDemoted={isDemoted}
        onRefreshMarketClick={!isDemoted ? () => refreshItemMarketMutation.mutate(params.id!) : undefined}
        refreshMarketLoading={refreshItemMarketMutation.isPending}
        onEnhanceClick={!isDemoted ? () => enhanceItemMutation.mutate(params.id!) : undefined}
        enhanceLoading={enhanceItemMutation.isPending}
        onRemoveBackgroundClick={!isDemoted ? () => removeItemBackgroundMutation.mutate(params.id!) : undefined}
        removeBackgroundLoading={removeItemBackgroundMutation.isPending}
        onMigrateImageClick={!isDemoted ? onMigrateImageModalOpen : undefined}
        migrateImageLoading={migrateItemImageMutation.isPending}
      />
    );
  };

  const renderTabs = () => {
    // Don't show tabs for demoted items
    if (isDemoted) {
      return null;
    }

    const labels = [
      <Flex gap="4px" alignItems="center" direction={{ base: 'column', md: 'row' }}>
        <Icon as={MdOutlineLocalOffer} />
        <Text>Listing Suggestions</Text>
      </Flex>,
      <Flex gap="4px" alignItems="center" direction={{ base: 'column', md: 'row' }}>
        <Icon as={AiOutlineLineChart} />
        <Text>Past Market</Text>
      </Flex>,
      <Flex gap="4px" alignItems="center" direction={{ base: 'column', md: 'row' }}>
        <Icon as={GiThorHammer} />
        <Text>Past Sales</Text>
      </Flex>,
      <Flex gap="4px" alignItems="center" direction={{ base: 'column', md: 'row' }}>
        <Icon as={IoStorefrontOutline} />
        <Text>Trading Floor</Text>
      </Flex>
    ];

    const content = [
      <Stack spacing={ItemDetailsSizingConfig.CONTENT_SPACING}>
        {renderListingSuggestions()}
      </Stack>,
      <Stack spacing={ItemDetailsSizingConfig.CONTENT_SPACING}>
        {renderMarketOverview()}
      </Stack>,
      <Stack spacing={ItemDetailsSizingConfig.CONTENT_SPACING}>
        {renderPastSales()}
      </Stack>,
      <Stack spacing={ItemDetailsSizingConfig.CONTENT_SPACING}>
        {renderTradingFloor()}
      </Stack>
    ];

    return (
      <TabGroup
        labels={labels}
        content={content}
        startingTab={0}
      />
    );
  };

  const renderModals = () => {
    const renderItemEditModalContent = () => {
      if (!item) return <Box />;

      return (
        <ItemDataSuggestionForm
          materialList={materialList}
          item={item}
          defaultFormData={getItemEditFormData()}
          onSubmit={onItemEditFormSubmit}
          submitLoading={updateItemMutation.isPending}
        />
      );
    };

    const renderMigrateImageModalContent = () => {
      if (!item) return <Box />;

      return (
        <MigrateImageWizard
          entity={item}
          entityType="item"
          isLoading={migrateItemImageMutation.isPending}
          onSubmit={handleMigrateImageSubmit}
          hasErrors={migrateItemImageMutation.isError}
        />
      );
    };

    const renderItemEditFieldModalContent = () => {
      if (!item) return <Box />;

      return (
        <ItemDataSuggestionForm
          materialList={materialList}
          item={item}
          defaultFormData={getItemEditFormData()}
          displayOptions={[modals.itemEditFieldModal]}
          requiredOptions={[modals.itemEditFieldModal]}
          onSubmit={onItemEditFormSubmit}
          submitLoading={updateItemMutation.isPending}
        />
      );
    };

    const renderRankChangeModalContent = () => {
      if (!item) return <Box />;

      return (
        <ItemRankChangeWizard
          item={item}
          isDemoted={isDemoted}
          onSubmit={onRankChangeSubmit}
          isLoading={isDemoted ? promoteItemMutation.isPending : demoteItemMutation.isPending}
          hasErrors={isDemoted ? promoteItemMutation.isError : demoteItemMutation.isError}
        />
      );
    };

    const renderCreateGroupingModalContent = () => {
      if (!item) return <Box />;

      return (
        <GroupingCreationWizard
          defaultArtistId={item.artistId}
          defaultItem={item}
          defaultListings={listingSuggestions?.data || []}
          initialWorkflow="item-first"
          onSubmit={onCreateGroupingSubmit}
          isLoading={createGroupingMutation.isPending}
        />
      );
    };

    return (
      <>
        <Modal
          isOpen={modals.itemEditModal}
          onClose={onItemEditModalClose}
          title="Edit Item"
          content={renderItemEditModalContent()}
          showCloseButton={false}
          showSubmitButton={false}
        />
        <Modal
          isOpen={!!modals.itemEditFieldModal}
          onClose={onItemEditFieldModalClose}
          title="Edit Item"
          content={renderItemEditFieldModalContent()}
          showCloseButton={false}
          showSubmitButton={false}
        />
        <Modal
          isOpen={modals.rankChange}
          onClose={onRankChangeModalClose}
          title={isDemoted ? 'Promote Item' : 'Demote Item'}
          content={renderRankChangeModalContent()}
          showCloseButton={false}
          showSubmitButton={false}
        />
        <Modal
          isOpen={modals.createGrouping}
          onClose={onCreateGroupingModalClose}
          title="Create Grouping"
          content={renderCreateGroupingModalContent()}
          showCloseButton={false}
          showSubmitButton={false}
          size="4xl"
        />
        <Modal
          isOpen={modals.migrateImage}
          onClose={onMigrateImageModalClose}
          title="Migrate Item Image"
          content={renderMigrateImageModalContent()}
          showCloseButton={false}
          showSubmitButton={false}
          size="2xl"
        />
      </>
    );
  };

  const renderDrawers = () => {
    return (
      <>
        <Drawer
          isOpen={drawers.marketInfoGuide}
          onClose={() => setDrawers(prev => ({ ...prev, marketInfoGuide: false }))}
          title="Market Guide"
          content={<InfoGuide text="Learn about the market" />}
        />
      </>
    );
  };

  const renderMarketStats = () => {
    const market = item.getMarket();
    if (market && item.hasMarketData()) {
      const content = [
        { title: 'Total Listings', stat: `${market.overview.totalListings}`, icon: <IoMdStats size={'2em'} /> },
        { title: 'Mean Price', stat: item.getOverviewMeanPriceForDisplay(), icon: <RiScalesLine size={'2em'} /> },
        { title: 'Max Price', stat: item.getOverviewMaxPriceForDisplay(), icon: <AiOutlineVerticalAlignTop size={'2em'} /> },
        { title: 'Growth', stat: item.getOverviewGrowthForDisplay(), icon: <AiOutlineLineChart size={'2em'} /> },
      ].map((block, index: number) => (
        <StatsCard key={`item_market_statcard_${index}`} title={block.title} stat={block.stat} icon={block.icon} />
      ));

      return (
        <SimpleGrid columns={[2, 2, 4]} spacing={ItemDetailsSizingConfig.CONTENT_SPACING}>
          {content}
        </SimpleGrid>
      );
    }
  }

  const renderMarketOverview = () => {
    return (
      <Box padding={{ base: "10px", md: "0px" }}>
        {renderMarketStats()}
        {renderPastMarket()}
      </Box>
    );
  }

  const renderPastMarket = () => {
    if (!item) return null;
    return (
      <SimpleGrid columns={[1, null, 2]} spacing={ItemDetailsSizingConfig.CONTENT_SPACING}>
        <MarketPriceChart
          market={item.getMarket()}
          listings={item.getSoldListings()}
          startingPrice={item.hasReleasePriceData() ? item.getReleaseConvertedPrice() : null}
        />
        <MarketVolumeChart
          market={item.getMarket()}
          listings={item.getSoldListings()}
        />
      </SimpleGrid>
    );
  };

  const renderPastSales = () => {
    if (itemListingsLoading) {
      return <EmptyState header="Loading..." description="Loading sales data" />;
    }

    if (!itemListings || itemListings.length === 0) {
      return <EmptyState header="No Sales Data" description="There is no sales data available for this item" />;
    }

    return (
      <Stack spacing={4}>
        <InfoGuide text="Past sales and auction results" />
        <ListingGrid
          gridListings={itemListings}
          onListingNameClick={handleListingNameClick}
          onListingItemClick={onItemNameClick}
          onListingArtistClick={onArtistClick}
          isLoading={itemListingsLoading}
        />
      </Stack>
    );
  };

  const renderTradingFloor = () => {
    if (!item) return null;
    return (
      <Stack spacing={4}>
        <InfoGuide text="Current market activity and trading" />
        <TradingFloor
          bids={item.activeMarket?.bids || []}
          asks={item.activeMarket?.asks || []}
          user={currentUser}
          onItemArtistNameClick={onArtistClick}
          onRemoveAskClick={onRemoveItemAskModalOpen}
          onRemoveBidClick={onRemoveItemBidModalOpen}
          onEditAskClick={onEditItemAskModalOpen}
          onEditBidClick={onEditItemBidModalOpen}
          onContactBidClick={onContactItemBidModalOpen}
          onSellBidClick={onSellItemModelOpen}
          onBuyAskClick={onBuyItemModelOpen}
          onContactAskClick={onContactItemAskModalOpen}
          isLoading={itemMarketLoading || itemActiveMarketLoading}
        />
      </Stack>
    );
  };


  if (itemLoading) {
    return <ItemContainerSkeleton />;
  }

  if (!item) {
    return <EmptyState header="Item Not Found" description="The requested item could not be found" />;
  }

  return (
    <>
      <Box maxWidth="1200px" width="100%" paddingTop={['80px', '100px', '100px']} justifySelf="center" minHeight="100vh">
        {renderContainer()}
        {renderItemInfoSlider()}
        {renderItemSuggestionGrid()}
        {renderTabs()}
      </Box>
      {renderModals()}
      {renderDrawers()}
    </>
  );
}

export default ItemDetailsView;
